type Range = { start: number; end: number };
type Run = { start: number; length: number; char: string };

const FENCE = "```";
const EMPHASIS = new Set(["*", "~"]);

export const withoutDanglingMarkdown = (text: string): string => {
  const { text: withoutOpenCode, code } = closedCode(text);
  const unclosed = unclosedEmphasis(withoutOpenCode, code);
  return removeRuns(withoutOpenCode, unclosed).trimEnd();
};

const closedCode = (text: string): { text: string; code: Range[] } => {
  const fences = occurrences(text, FENCE, []);
  const fenceOpen = fences.length % 2 === 1;
  const open = fenceOpen ? fences[fences.length - 1] : text.length;
  const withoutFence = fenceOpen ? text.slice(0, open) + text.slice(open + FENCE.length) : text;
  const blocks = [...pairs(fences.slice(0, fenceOpen ? -1 : undefined), FENCE.length)];
  if (fenceOpen) blocks.push({ start: open, end: withoutFence.length });
  const ticks = occurrences(withoutFence, "`", blocks);
  if (fenceOpen || ticks.length % 2 === 0) return { text: withoutFence, code: [...blocks, ...pairs(ticks, 1)] };
  const tick = ticks[ticks.length - 1];
  const withoutTick = withoutFence.slice(0, tick) + withoutFence.slice(tick + 1);
  return { text: withoutTick, code: [...blocks, ...pairs(ticks.slice(0, -1), 1), { start: tick, end: withoutTick.length }] };
};

const unclosedEmphasis = (text: string, code: Range[]): Run[] => {
  const open: Run[] = [];
  for (const run of emphasisRuns(text, code)) {
    const before = text[run.start - 1];
    const after = text[run.start + run.length];
    const canOpen = after === undefined || !/\s/.test(after);
    const canClose = before !== undefined && !/\s/.test(before);
    const top = open[open.length - 1];
    if (canClose && top && top.char === run.char && top.length === run.length) {
      open.pop();
    } else if (canOpen) {
      open.push(run);
    }
  }
  return open;
};

const emphasisRuns = (text: string, code: Range[]): Run[] => {
  const runs: Run[] = [];
  let index = 0;
  while (index < text.length) {
    const char = text[index];
    if (!EMPHASIS.has(char) || inside(code, index)) {
      index += 1;
      continue;
    }
    let end = index;
    while (text[end] === char) end += 1;
    const length = end - index;
    if (char === "*" || length === 2) runs.push({ start: index, length, char });
    index = end;
  }
  return runs;
};

const removeRuns = (text: string, runs: Run[]): string =>
  [...runs]
    .sort((a, b) => b.start - a.start)
    .reduce((current, run) => current.slice(0, run.start) + current.slice(run.start + run.length), text);

const occurrences = (text: string, marker: string, skip: Range[]): number[] => {
  const found: number[] = [];
  let index = text.indexOf(marker);
  while (index !== -1) {
    if (!inside(skip, index)) found.push(index);
    index = text.indexOf(marker, index + marker.length);
  }
  return found;
};

const pairs = (positions: number[], width: number): Range[] => {
  const ranges: Range[] = [];
  for (let i = 0; i + 1 < positions.length; i += 2) ranges.push({ start: positions[i], end: positions[i + 1] + width });
  return ranges;
};

const inside = (ranges: Range[], index: number): boolean => ranges.some((r) => index >= r.start && index < r.end);
